import React, {useContext, useMemo, useState, useRef, useEffect, useCallback} from "react";
import { useRouter } from "next/router";
import {Tip, Box, ResponsiveContext, Spinner, Text} from "grommet";
import {formatCurrency} from "../../../utils/numbers";
import {formatDate, formatDateUTC} from "../../../utils/dates";
import Badge from "../../Badge";
import { sentencecase, uncamelcase, unsnakecase } from "../../../utils/string";
import {formatName} from "../../../utils/names";
import DataTableWithBackendSearch from "../../DataTableWithBackendSearch";
import { getQueryString } from "../../../utils/search";
import DottedText from "../../DottedText";
import EditableCustomerTag from "../../EditableCustomerTag";
import InvoiceTag from "../../InvoiceTag";
import {
    faArrowRightArrowLeft as IconArrowsLeftRight,
    faCircleExclamation as IconAlertCircle,
} from '@fortawesome/pro-light-svg-icons';
import FontAwesomeIcon from "../../FontAwesomeIcon";
import { handleFileDownloadErrors, handleErrors } from "../../../utils/errors";
import { NotificationsContext } from "../../NotificationsProvider";
import dynamic from "next/dynamic";
import TransactionFilter from "../../filters/TransactionFilter";
import SearchCustomerFilter from "../../search/filters/SearchCustomerFilter";
import {DataTablePreferenceContext} from "../../DataTablePreferenceProvider";
import { setTransactionNavigationData } from "../../../utils/invoiceNavigation";
import { removeTransactionFromList } from "../../../utils/transactionDismissal";
import Button from "../../Button";
import { useSWRConfig } from "swr";
import styled from "styled-components";

const SendCSVModal = dynamic(() => import('../../SendCSVModal'))
const DismissTransactionModal = dynamic(() => import('./DismissTransactionModal'))

// Row actions rest hidden and appear on hover. Kept at opacity 0 rather than display:none
// so the column holds its width and the row can't shift as the cursor moves down the table.
// pointer-events is what actually stops a stray click landing on an invisible button.
const HoverRowActions = styled(Box)`
    opacity: 0;
    pointer-events: none;

    .rdt_TableRow:hover &,
    &:focus-within {
        opacity: 1;
        pointer-events: auto;
    }
`;

const TransactionList = ({
    title,
    hideCustomer = false,
    pendingTxns = false,
    hideFees = false,
    hidePayouts = false,
    hideDateRangeFilter=false,
    hideAmountRangeFilter = false,
    dataTableWrapperProps,
    filter,
    hideIcon=false,
    customerId,
    customerGroupId,
    additionalParam={},
    hideSearch,
    onRowClicked,
    hideTableIfNoData=false,
    onDataChange,
    isPending=false,
    storeNavigationData=false,
    hideInvoice=false,
    allowDismiss=false,
    onMatch,
    onDismissed,
    ...rest
}) => {
    const disabledSort = pendingTxns;
    const [reshapedData, setReshapedData] = useState([]);
    const router = useRouter();
    const { customer_filter: customerFilterQuery } = router.query;
    const size = useContext(ResponsiveContext);
    const notificationCtx = useContext(NotificationsContext);
    const dataTablePreferenceCtx = useContext(DataTablePreferenceContext);
    const isMobile = size === "small";
    const [csvQueryUrl, setCsvQueryUrl] = useState();
    const currentQueryUrl = useRef(null); // Used for exporting csv with correct params
    const [showSendCSVModal, setShowSendCSVModal] = useState(false);
    const [refetchKey, setRefetchKey] = useState(0);
    const {mutate} = useSWRConfig();
    const [dismissTarget, setDismissTarget] = useState(null);

    const dismissTransaction = async (row, {reason, note}) => {
        try {
            const response = await fetch(`/api/transactions/${row.id}/dismiss`, {
                method: 'POST',
                headers: {'Content-Type': 'application/json'},
                body: JSON.stringify({reason, note})
            });
            await handleErrors(response);
            // Drop the row from the cached page rather than refetching. currentQueryUrl is
            // the same string this list uses as its SWR key.
            if (currentQueryUrl.current) {
                mutate(
                    currentQueryUrl.current,
                    previous => removeTransactionFromList(previous, row.id),
                    { revalidate: false }
                );
            }
            notificationCtx.showNotification({type: 'success', message: 'Payment dismissed from review'});
            onDismissed && onDismissed(row);
            return true;
        } catch (error) {
            // handleErrors rejects with the parsed API body when there is one, but with a
            // SyntaxError when there is none (the route answers 401 with an empty body) and
            // fetch itself throws on a dropped connection. Notification keys its critical
            // styling off `type` and renders `message` as the title, so passing a raw Error
            // straight through would show a neutral toast titled "Unexpected end of JSON
            // input" and the merchant would read the closing modal as success.
            notificationCtx.showNotification(
                error?.type
                    ? error
                    : {type: 'error', message: 'Could not dismiss this payment. Please try again.'}
            );
            return false;
        }
    };

    // The columns memo is keyed on a few flags rather than rebuilding every render, so the
    // cells reach the latest onMatch through a ref instead of closing over a stale one.
    // setDismissTarget is a setState, which is already stable.
    const onMatchRef = useRef(onMatch);
    onMatchRef.current = onMatch;
    const showMatch = Boolean(onMatch);

    /**
     * The two ways out of this row. Both are labelled, since dismiss has no undo yet and an
     * unlabelled icon is a thin affordance for that, and showing them together keeps the
     * constructive path as visible as the destructive one.
     *
     * On a pointer the pair stays hidden until the row is hovered: repeated down every row
     * they read as noise over the data the merchant is actually scanning. Keyboard users
     * reach them through :focus-within, and touch has no hover so it always shows them.
     */
    const renderRowActions = (datum) => {
        // On mobile the actions get their own line under a left-aligned stack, so they
        // anchor to that same edge. Floating them right would leave an L-shaped void
        // between the content and the buttons. On desktop they belong in a right-aligned
        // column like every other action.
        const justify = isMobile ? 'start' : 'end';

        // A payment can span several invoices, and the backend allocates asynchronously, so
        // a row sits here matching for a while with no referencable_id yet. The Invoice
        // column used to carry that spinner; it is hidden on unmatched-only lists, so the
        // progress state moves here rather than vanishing. Always visible, never on hover:
        // it is status, not an action, and there is nothing to act on until it finishes.
        if (datum.allocation_pending) {
            return (
                <Box direction="row" gap="xsmall" align="center" justify={justify}>
                    <Spinner size="xsmall" />
                    <Text size="small" color="text-weak">Matching...</Text>
                </Box>
            );
        }

        const Container = isMobile ? Box : HoverRowActions;
        return (
            <Container direction="row" gap="xsmall" align="center" justify={justify}>
                {showMatch && (
                    <Button
                        event={{section: 'transaction_list', element: 'match_transaction'}}
                        size="small"
                        secondary
                        label="Match"
                        onClick={() => onMatchRef.current && onMatchRef.current(datum)}
                    />
                )}
                {allowDismiss && (
                    // The theme's default variant: same size and weight as Match, but
                    // borderless, so the two read as one pair with the constructive action
                    // carrying the emphasis.
                    <Button
                        event={{section: 'transaction_list', element: 'dismiss_transaction'}}
                        size="small"
                        label="Dismiss"
                        onClick={() => setDismissTarget(datum)}
                    />
                )}
            </Container>
        );
    };

    const hasRowActions = showMatch || allowDismiss;

    // Parse customer filter from URL on mount
    const parseCustomerFilterFromUrl = useCallback(() => {
        if (!customerFilterQuery) return [];
        return customerFilterQuery.split(',').map(item => {
            const [type, id] = item.split(':');
            return { id: parseInt(id), _type: type === 'group' ? 'group' : 'customer' };
        }).filter(item => item.id);
    }, [customerFilterQuery]);

    const [selectedCustomers, setSelectedCustomers] = useState(() => parseCustomerFilterFromUrl());

    // Sync customer filter to URL (only for non-pending, non-customer-specific views)
    useEffect(() => {
        if (hideCustomer || pendingTxns || !storeNavigationData) return;

        const customerFilter = selectedCustomers.length > 0
            ? selectedCustomers.map(item => {
                const type = item._type === 'group' ? 'group' : 'customer';
                return `${type}:${item.id}`;
            }).join(',')
            : null;

        const currentQuery = { ...router.query };
        if (customerFilter) {
            currentQuery.customer_filter = customerFilter;
        } else {
            delete currentQuery.customer_filter;
        }

        // Build URL without encoding commas
        const queryString = Object.entries(currentQuery)
            .filter(([_, v]) => v !== null && v !== undefined && v !== '')
            .map(([k, v]) => `${k}=${v}`)
            .join('&');
        const asPath = queryString ? `${router.pathname}?${queryString}` : router.pathname;

        router.replace(
            { pathname: router.pathname, query: currentQuery },
            asPath,
            { shallow: true }
        );
    }, [selectedCustomers, hideCustomer, pendingTxns, storeNavigationData]);

    // Combined filter component (only for non-pending transactions)
    const CombinedFilters = ({ filterValues, onApplyFilter }) => (
        <Box direction="row" gap={{ column: 'small', row: 'small' }} wrap>
            <TransactionFilter filterValues={filterValues} onApplyFilter={onApplyFilter} />
            {!hideCustomer && (
                <SearchCustomerFilter
                    selectedItems={selectedCustomers}
                    onApplyFilter={setSelectedCustomers}
                />
            )}
        </Box>
    );

    // Stats component for pending transactions
    const Stats = ({transactions}) => {
        const totalAmount = transactions?.reduce((sum, txn) => sum + (txn.amount || 0), 0) || 0;
        return(
            <Box>
                <Text color="text-weak" size="small">Total pending: {`${formatCurrency(totalAmount)} across ${transactions?.length || 0} transaction${transactions?.length === 1 ? '' : 's'}`}</Text>
            </Box>
        )
    };
    const columns = useMemo(()=>{
        return isMobile ? [
            {
                selector: row => row.id,
                cell: (datum) => {
                    return (
                        <Box data-tag="allowRowEvents" fill="horizontal" gap="xsmall" pad={{right: 'xsmall', vertical: 'xsmall'}}>
                            {/* align="start" so the amount sits on the same line as the type
                                rather than floating against the middle of the stack. */}
                            <Box data-tag="allowRowEvents" fill="horizontal" justify="between" direction="row" gap="small" align="start">
                                <Box data-tag="allowRowEvents">
                                    <Text data-tag="allowRowEvents">{sentencecase(unsnakecase(datum?.display_type))}</Text>
                                    <Text data-tag="allowRowEvents" size="small" color="text-weak">
                                        {datum?.recipient?.display_name || formatName(datum?.recipient)}
                                        {/* The separator only earns its place when there is an
                                            invoice after it. On unmatched-only lists there never
                                            is, so it would dangle on every row. */}
                                        {datum?.invoice_number ? ` · ${datum.invoice_number}` : ''}
                                    </Text>
                                    {datum?.allocation_pending ? (
                                        <Spinner size="xsmall" />
                                    ) : (
                                        <Text data-tag="allowRowEvents" size="xsmall" color="text-xweak">{datum?.formatted_created_at}</Text>
                                    )}
                                </Box>
                                <Box data-tag="allowRowEvents">
                                    <Text data-tag="allowRowEvents" wordBreak="keep-all">{formatCurrency(datum?.amount, datum?.currency)}</Text>
                                </Box>
                            </Box>
                            {/* Own line rather than squeezed beside the amount, and with no
                                allowRowEvents tag: react-data-table only fires the row click
                                for tagged targets, so these won't also navigate. */}
                            {hasRowActions && renderRowActions(datum)}
                        </Box>
                    )
                }
            }
        ] : [
            {
                selector: row => row.created_at,
                name: 'Date',
                sortable: !disabledSort,
                sortField: 'created_at',
                grow: 1.5,
                cell: (datum) => datum.formatted_created_at,
            },
            {
                selector: row => row.display_type,
                name: 'Type',
                grow: 1.2,
                /*
                    Tagged because react-data-table only fires the row click when the
                    clicked element itself carries the tag, and it puts the tag on the
                    cell rather than on whatever the cell renders. Without it this
                    column is a dead patch in an otherwise clickable row.
                */
                cell: (datum) => (
                    <Box data-tag="allowRowEvents" direction="row" gap="xsmall">
                        {uncamelcase(unsnakecase(datum.display_type))}
                    </Box>
                )
            },
            {
                selector: row => row.amount,
                name: 'Amount',
                right: true,
                sortable: !disabledSort,
                type: "currency",
                sortField: 'amount',
                cell: (datum) => formatCurrency(datum.amount, datum.currency)
            },
            !hideCustomer && {
                selector: row => row.display_name,
                name: 'Customer',
                sortable: !disabledSort,
                grow: 2,
                sortField: 'company_name',
                cell: (datum) => datum?.recipient?.id && (
                    <EditableCustomerTag
                        customerId={datum.recipient.id}
                        transactionId={datum.id}
                        editable={!datum.referencable_id}
                        onSuccess={() => setRefetchKey(k => k + 1)}
                        inline
                    />
                )
            },
            // Worth hiding on lists that only ever hold unmatched payments: a row leaves
            // those the moment it gains a referencable_id, so every cell here would show
            // the same "no invoice" warning and never anything else.
            !hideInvoice && {
                selector: row => row.invoice_number,
                name: 'Invoice',
                sortable: true,
                sortField: 'identifier',
                grow: 2,
                cell: (datum) => {
                    if (datum.allocation_pending) {
                        return (
                            <Box data-tag="allowRowEvents" align="center" width="xxsmall">
                                <Spinner size="xsmall" />
                            </Box>
                        )
                    }
                    if (!datum.referencable_id) {
                        return (
                            <Tip content="Select transaction to link invoice.">
                                <Box data-tag="allowRowEvents" align="center" width="xxsmall">
                                    <FontAwesomeIcon color="status-warning" SelectedIcon={IconAlertCircle}/>
                                </Box>
                            </Tip>
                        )
                    }
                    if (datum.identifiers && datum.identifiers.length > 1) {
                        return (
                            <Tip content={(
                                <Box pad="small" width={{max: 'medium'}}>
                                    {(datum.identifiers).map(identifier => (
                                        <Box key={identifier} margin="xxsmall">
                                            <Badge>{identifier}</Badge>
                                        </Box>
                                    ))}
                                </Box>
                            )}>
                                <DottedText data-tag="allowRowEvents" size="small">
                                    {datum.identifiers.length} invoices
                                </DottedText>
                            </Tip>)
                    }
                    if (datum.referencable_id && datum.type !== 'Invoice') return <InvoiceTag id={datum.referencable_id} inline />;
                },
            },
            hasRowActions && {
                name: '',
                right: true,
                grow: 1.4,
                // Without this the actions would also open the row: react-data-table skips
                // tagging cells marked ignoreRowClick, and only fires onRowClicked for
                // tagged targets.
                ignoreRowClick: true,
                cell: (datum) => renderRowActions(datum),
            },
        ].filter(Boolean)
    },[isMobile, hideInvoice, hasRowActions, allowDismiss, showMatch]);

    const getTransactionListCSV = ({callback, url, email}) => {
        const exportError = {type: 'error', message: 'Unable to download csv data. Try again later.'};
        const exportSuccessMessage = {type: 'success', message: `Your file will be sent to ${email}`};
        const queryURL = email ? url + `&email=${email}` : url;
        fetch(queryURL, {
            method: 'GET'
        })
            .then(handleFileDownloadErrors)
            .then((data) => {
                data ?
                    notificationCtx.showNotification(exportSuccessMessage) :
                    notificationCtx.showNotification(exportError);
                callback && callback();
            })
            .catch(e => notificationCtx.showNotification(exportError));
    };

    return (
        <Box>
            {showSendCSVModal && (
                <SendCSVModal
                    onClose={() => {setShowSendCSVModal()}}
                    onSubmit={(email) => {
                        getTransactionListCSV({email: email, url: csvQueryUrl})
                    }}
                />
            )}
            {dismissTarget && (
                <DismissTransactionModal
                    onClose={() => setDismissTarget(null)}
                    onSubmit={({reason, note}) => dismissTransaction(dismissTarget, {reason, note})}
                />
            )}
            <DataTableWithBackendSearch
                syncFiltersToUrl={storeNavigationData} // Only sync URL for main transaction list (not pending or unknown payments)
                filterParams={{
                    defaultFilter: dataTablePreferenceCtx.getPreference({table: 'transactions', key: 'filters'})
                }}
                filterControls={pendingTxns ? () => <Stats transactions={reshapedData} /> : CombinedFilters}
                noTableHead={isMobile}
                columns={columns}
                emptyListMessage={`No ${title ? title.toLowerCase() : 'data'} available`}
                conditionalRowStyles={[{
                    when: row => row.allocation_pending,
                    style: { opacity: 0.6, cursor: 'default', pointerEvents: 'none' }
                }]}
                reshape={(data) => {
                    if (!data) return null;
                    const reshaped = data.transactions.map((transaction, index) => {
                        const {
                            amount, order, created_at, recipient, payout_date
                        } = transaction;
                        const companyName = recipient ? recipient.company_name : '-';
                        const customerName = formatName(recipient, true, '-');
                        const displayName = recipient ? (recipient?.company_name || formatName(recipient, true, '')) : '-';
                        return {
                            ...transaction,
                            amount: parseFloat(amount),
                            formatted_created_at: formatDate(created_at),
                            formatted_payout_date: payout_date ? formatDateUTC(payout_date) : '',
                            created_at: new Date(created_at),
                            company_name: companyName,
                            display_name: displayName,
                            customer_name: customerName,
                            invoice_number: order?.identifier || '',
                            index: index,
                            customer: (recipient && recipient?.company_name) ? companyName : customerName
                        };
                    });
                    setReshapedData(reshaped);
                    return reshaped;
                }}
                refetchUrlIfTheseParamsChange={{customerGroupId, customerId, selectedCustomers}}
                getUrl={ ({filterQuery,...rest}) => {
                    // Build customer_filter param from selected customers
                    const customerFilter = selectedCustomers.length > 0
                        ? selectedCustomers.map(item => {
                            const type = item._type === 'group' ? 'group' : 'customer';
                            return `${type}:${item.id}`;
                        }).join(',')
                        : null;

                    let params = {
                        transaction_type: (filterQuery || []).join(','),
                        status: pendingTxns ? "pending" : 'settled',
                        ...(customerGroupId && { customer_group_id: customerGroupId }),
                        ...(customerId && !customerGroupId && { customer_id: customerId }),
                        ...(customerFilter && { customer_filter: customerFilter }),
                        ...additionalParam
                    }
                    return getQueryString({
                        ...rest,
                        baseUrl: 'api/transactions',
                        optionalParams: params
                    });
                }}
                key={`${customerId}-${refetchKey}`}
                dataTableWrapperProps={dataTableWrapperProps}
                {...(title ? {title} : {})}
                hideSearch={pendingTxns || hideSearch}
                hideDateRangeFilter={pendingTxns || hideDateRangeFilter}
                hideAmountRangeFilter={pendingTxns || hideAmountRangeFilter}
                hideExport={pendingTxns}
                keyField={'index'}
                hidePagination={pendingTxns}
                onExportCSVClicked={() => setShowSendCSVModal(true)}
                {...(hideIcon ? {} : { icon: IconArrowsLeftRight })}
                setQueryUrlCallback={(url) => { // get query url from since data table controls the state of filters and sorting
                    const queryParams = url.split("?")[1]; // get only the url
                    setCsvQueryUrl("/api/transactions/csv?" + queryParams);
                    currentQueryUrl.current = url; // Store for navigation
                }}
                onRowClicked={onRowClicked}
                dataName="transactions"
                hideTableIfNoData={hideTableIfNoData}
                onDataChange={(transactions, rawData) => {
                    // Store navigation data if enabled (for settled transactions list)
                    if (storeNavigationData && transactions && transactions.length > 0) {
                        const ids = transactions.map(txn => txn.id);
                        const total = rawData?.total || transactions.length;
                        const currentOffset = parseInt(new URLSearchParams(currentQueryUrl.current?.split('?')[1] || '').get('offset') || '0');
                        const hasMore = (currentOffset + transactions.length) < total;
                        setTransactionNavigationData({
                            tokens: ids,
                            queryUrl: currentQueryUrl.current,
                            hasMore,
                            total
                        });
                    }
                    // Call parent's onDataChange if provided
                    onDataChange && onDataChange(transactions, rawData);
                }}
                {...rest}
            />
        </Box>
    );
};

export default TransactionList;
